import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  login,
  completeNewPassword,
  submitTotpChallenge,
  verifyTotpEnrollment,
  type LoginResult,
  type CognitoUser,
} from '../lib/cognito';
import { useAuth } from '../lib/auth-context';

type FlowState =
  | { kind: 'credentials' }
  | { kind: 'new-password'; user: CognitoUser; userAttributes: Record<string, string> }
  | { kind: 'mfa-setup'; user: CognitoUser; secretCode: string }
  | { kind: 'mfa-challenge'; user: CognitoUser };

/**
 * Phase 1 login — handles:
 *   1. Email + password
 *   2. NEW_PASSWORD_REQUIRED (first login after admin-create-user)
 *   3. MFA TOTP enrollment (first MFA setup)
 *   4. MFA TOTP challenge (every subsequent login)
 *
 * UI intentionally minimal; Radix primitives + keyboard-first.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [flow, setFlow] = useState<FlowState>({ kind: 'credentials' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const afterSuccess = async () => {
    await auth.refresh();
    const from = (location.state as { from?: string } | null)?.from ?? '/tenants';
    navigate(from, { replace: true });
  };

  const handleResult = (result: LoginResult) => {
    switch (result.kind) {
      case 'success':
        void afterSuccess();
        break;
      case 'new-password-required':
        setFlow({ kind: 'new-password', user: result.user, userAttributes: result.userAttributes });
        break;
      case 'mfa-setup':
        setFlow({ kind: 'mfa-setup', user: result.user, secretCode: result.secretCode });
        break;
      case 'mfa-challenge':
        setFlow({ kind: 'mfa-challenge', user: result.user });
        break;
      case 'none':
        setError('unexpected empty challenge');
        break;
    }
  };

  const onCredentials = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      const result = await login(
        String(form.get('email')),
        String(form.get('password')),
      );
      handleResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login failed');
    } finally {
      setLoading(false);
    }
  };

  const onNewPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (flow.kind !== 'new-password') return;
    setError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      const result = await completeNewPassword(
        flow.user,
        String(form.get('newPassword')),
        {}, // no additional required attributes — pool schema has none mandatory beyond email
      );
      handleResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'password update failed');
    } finally {
      setLoading(false);
    }
  };

  const onMfaSetup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (flow.kind !== 'mfa-setup') return;
    setError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      await verifyTotpEnrollment(flow.user, String(form.get('totp')));
      // After successful enrollment, Cognito requires a re-authentication.
      // Simplest flow: send user back to credentials form.
      setFlow({ kind: 'credentials' });
      setError('MFA enrolled — please sign in again with your new password + TOTP.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'TOTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const onMfaChallenge = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (flow.kind !== 'mfa-challenge') return;
    setError(null);
    setLoading(true);
    const form = new FormData(e.currentTarget);
    try {
      const result = await submitTotpChallenge(flow.user, String(form.get('totp')));
      handleResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'TOTP challenge failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg p-8">
      <div className="panel w-full max-w-sm p-6 space-y-4">
        <div>
          <div className="text-lg font-semibold">StrattoVoice Admin</div>
          <div className="text-xs text-muted mt-0.5">
            Internal operations console · {import.meta.env.MODE}
          </div>
        </div>

        {error && (
          <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-3 py-2">
            {error}
          </div>
        )}

        {flow.kind === 'credentials' && (
          <form onSubmit={onCredentials} className="space-y-3">
            <Field label="Email" name="email" type="email" autoFocus required autoComplete="email" />
            <Field label="Password" name="password" type="password" required autoComplete="current-password" />
            <SubmitButton label="Sign in" loading={loading} />
          </form>
        )}

        {flow.kind === 'new-password' && (
          <form onSubmit={onNewPassword} className="space-y-3">
            <div className="text-xs text-muted">
              First-login password reset. Minimum 14 chars, mixed case, number, symbol.
            </div>
            <Field label="New password" name="newPassword" type="password" autoFocus required />
            <SubmitButton label="Set password" loading={loading} />
          </form>
        )}

        {flow.kind === 'mfa-setup' && (
          <form onSubmit={onMfaSetup} className="space-y-3">
            <div className="text-xs text-muted space-y-1">
              <div>Scan this secret with your authenticator app, then enter a code:</div>
              <code className="block text-xs font-mono bg-bg p-2 rounded border border-border break-all">
                {flow.secretCode}
              </code>
            </div>
            <Field label="TOTP code" name="totp" type="text" autoFocus required inputMode="numeric" pattern="[0-9]{6}" />
            <SubmitButton label="Verify" loading={loading} />
          </form>
        )}

        {flow.kind === 'mfa-challenge' && (
          <form onSubmit={onMfaChallenge} className="space-y-3">
            <div className="text-xs text-muted">Enter the 6-digit code from your authenticator app.</div>
            <Field label="TOTP code" name="totp" type="text" autoFocus required inputMode="numeric" pattern="[0-9]{6}" />
            <SubmitButton label="Sign in" loading={loading} />
          </form>
        )}
      </div>
    </div>
  );
}

function Field(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, ...rest } = props;
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted">{label}</span>
      <input
        {...rest}
        className="w-full px-2 py-1.5 text-sm bg-bg border border-border rounded focus:border-accent outline-none"
      />
    </label>
  );
}

function SubmitButton({ label, loading }: { label: string; loading: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full bg-accent text-accent-fg text-sm font-medium py-1.5 rounded hover:opacity-95 disabled:opacity-60"
    >
      {loading ? '…' : label}
    </button>
  );
}
